import Database from "@tauri-apps/plugin-sql";
import { onActiveRootChange, resolvePath } from "@/services/drives";
import { logger } from "@/services/logger";
import { runMigrations } from "./schema";

const log = logger.scope("sqlite");

export const DB_RELATIVE_PATH = "Storage/database/seufic.db";

let dbPromise: Promise<Database> | null = null;

async function open(): Promise<Database> {
  const path = await resolvePath(DB_RELATIVE_PATH);
  const db = await Database.load(`sqlite:${path}`);
  await db.execute("PRAGMA foreign_keys = ON");
  await db.execute("PRAGMA busy_timeout = 5000");
  await runMigrations(db);
  log.info("Database ready", { path });
  return db;
}

/** The process-wide SQLite connection, opened against whichever root is
 * currently active (drive or local fallback). Every sqlite-backed repository
 * shares this single connection/schema — conversations, memory, knowledge
 * all live in the one file, matching the "Seufic Drive is the home for
 * everything" framing in `DRIVE_ARCHITECTURE.md`.
 *
 * A failed open (corrupt file, disk full, drive pulled mid-open) clears the
 * cache rather than latching a rejected promise forever — `??=` alone would
 * cache the failure permanently, since a rejected promise is still a truthy
 * object. This way the *next* call (after the user restores a backup, or
 * the drive comes back) gets a fresh attempt instead of a dead app. */
export function getDb(): Promise<Database> {
  dbPromise ??= open().catch((err: unknown) => {
    dbPromise = null;
    log.error("Failed to open the database", err);
    throw err;
  });
  return dbPromise;
}

/** Forces the next `getDb()` call to reconnect — used by tests and by
 * anything that needs a clean handle after an external change. */
export function resetDbConnection(): void {
  dbPromise = null;
}

/** Closes the live connection and waits for it — needed before overwriting
 * the database file on disk (e.g. restoring a backup), since a copy over an
 * open handle can fail or corrupt state, especially on Windows. */
export async function closeDbConnection(): Promise<void> {
  if (!dbPromise) return;
  const db = await dbPromise;
  dbPromise = null;
  await db.close();
}

onActiveRootChange(() => {
  const previous = dbPromise;
  dbPromise = null;
  if (previous) {
    void previous
      .then((db) => db.close())
      .catch((err: unknown) => log.warn("Failed to close previous database cleanly", err));
  }
});
